import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getAdminMobileTitleKey } from "@/lib/navigation/mobile-title";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { FcmBootstrap } from "@/modules/notifications/components/FcmBootstrap";
import { NotificationPromptBanner } from "@/modules/notifications/components/NotificationPromptBanner";
import { NotificationBell } from "@/modules/notifications/components/NotificationBell";
import { LogoutButton } from "./LogoutButton";
import { LanguageSelect } from "./LanguageSelect";
import { ThemeToggle } from "./ThemeToggle";
import { Sidebar } from "./Sidebar";
import { BottomNav } from "./BottomNav";
import { useAdminNav } from "./nav";
import { HomeNavIcon, ProfileNavIcon } from "./nav-icons";

type AppShellProps = {
  locale: string;
  isSuperAdmin?: boolean;
  adminId?: string;
  adminName?: string;
  adminRoles?: string[];
  firebaseToken?: string | null;
  children: React.ReactNode;
};

const loginSuffix = "/admin/login";
const SIDEBAR_COLLAPSED_KEY = "admin_sidebar_collapsed";

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell({
  locale,
  isSuperAdmin,
  adminId,
  adminName,
  adminRoles,
  firebaseToken,
  children,
}: AppShellProps) {
  const { pathname } = useLocation();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const isLoginRoute = pathname?.endsWith(loginSuffix);
  const [sidebarHidden, setSidebarHidden] = useState(readSidebarCollapsed);
  const mainRef = useRef<HTMLElement>(null);
  // Mobile header title fades in once the page's own large title has scrolled
  // away — same behavior as the seller portal's collapsing screen headers.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = mainRef.current;
    if (!el || !isMobile) return;
    const onScroll = () => setScrolled(el.scrollTop > 56);
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  const { groups, tabs, activeItem } = useAdminNav({ locale, isSuperAdmin });

  const dashboardHref = `/${locale}/admin`;
  const profileHref = `/${locale}/admin/profile`;
  const isProfileRoute = pathname?.startsWith(profileHref);

  const pageTitle = useMemo(
    () => t(getAdminMobileTitleKey(pathname)),
    [pathname, t],
  );

  const roleLabel = useMemo(() => {
    const roles = adminRoles ?? [];
    if (roles.includes("SUPER_ADMIN")) return t(keys.admin.roleSuperAdmin);
    if (roles.includes("ADMIN")) return t(keys.admin.roleAdmin);
    if (roles.includes("CUSTOMER_SERVICE"))
      return t(keys.admin.roleCustomerService);
    if (roles.includes("SALES")) return t(keys.admin.roleSales);
    return null;
  }, [adminRoles, t]);

  const identityName = adminName?.trim() || t(keys.common.appName);
  const identityInitial = identityName.charAt(0).toUpperCase();

  const toggleSidebar = () => {
    setSidebarHidden((hidden) => {
      const next = !hidden;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  if (isLoginRoute) {
    return <>{children}</>;
  }

  return (
    <div className="admin-shell flex h-dvh w-full overflow-hidden bg-[color:var(--canvas)]">
      <FcmBootstrap adminId={adminId} firebaseToken={firebaseToken} />

      {!isMobile ? (
        <Sidebar
          dashboardHref={dashboardHref}
          profileHref={profileHref}
          groups={groups}
          hidden={sidebarHidden}
          onHide={toggleSidebar}
          identity={{
            name: identityName,
            roleLabel,
            initial: identityInitial,
          }}
          footerSlot={
            <div className="flex flex-col gap-3">
              <ThemeToggle />
              <LanguageSelect currentLocale={locale} />
              <LogoutButton
                redirectTo={`/${locale}/admin/login`}
                className="h-9 w-full rounded-[10px] px-3 text-xs font-semibold text-[color:var(--text-muted)]"
              />
            </div>
          }
        />
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {!isMobile ? (
          <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[color:var(--line)] bg-[color:var(--card)] px-6 text-[color:var(--text-primary)]">
            {sidebarHidden ? (
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label={t(keys.admin.navExpand)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] hover:text-[color:var(--text-primary)]"
              >
                <PanelIcon />
              </button>
            ) : null}
            <div className="flex min-w-0 items-center gap-2 text-[15px] font-semibold text-[color:var(--text-primary)]">
              <span
                aria-hidden="true"
                className="inline-flex shrink-0 [&_svg]:h-[19px] [&_svg]:w-[19px]"
              >
                {activeItem?.icon ??
                  (isProfileRoute ? <ProfileNavIcon /> : <HomeNavIcon />)}
              </span>
              <span className="truncate">{activeItem?.label ?? pageTitle}</span>
            </div>
            <div className="ms-auto flex items-center gap-2">
              <NotificationBell adminId={adminId} locale={locale} />
            </div>
          </header>
        ) : (
          <header
            className={[
              "shrink-0 border-b bg-[color:var(--canvas)] transition-colors duration-200",
              scrolled ? "border-[color:var(--hair)]" : "border-transparent",
            ].join(" ")}
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
          >
            <div className="flex h-[52px] items-center gap-2 px-4">
              <span
                aria-hidden={!scrolled}
                className={[
                  "min-w-0 flex-1 truncate text-[15px] font-semibold text-[color:var(--text-primary)] transition-opacity duration-200",
                  scrolled ? "opacity-100" : "opacity-0",
                ].join(" ")}
              >
                {pageTitle}
              </span>
              <NotificationBell adminId={adminId} locale={locale} />
            </div>
          </header>
        )}

        <main
          ref={mainRef}
          className="admin-main no-scrollbar flex min-h-0 flex-1 justify-center overflow-y-auto overscroll-y-contain bg-[color:var(--canvas)]"
          data-scroll-container="portal"
        >
          <div className="w-full max-w-6xl px-4 py-4 sm:px-6 sm:py-6">
            <NotificationPromptBanner />
            {children}
            {/* Clearance for the floating mobile nav pill (fixed, ~76px + safe area). */}
            <div className="h-24 lg:hidden" aria-hidden="true" />
          </div>
        </main>
      </div>

      {isMobile ? (
        <BottomNav
          tabs={tabs}
          identity={{
            name: identityName,
            initial: identityInitial,
            href: profileHref,
          }}
          locale={locale}
        />
      ) : null}
    </div>
  );
}

function PanelIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
    </svg>
  );
}
